import { useCallback, useRef, ChangeEvent } from 'react';
import { t, Trans } from '@lingui/macro';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Message from '@decisiv/ui-components/lib/components/Message';

import { P, H3, H4 } from '@decisiv/ui-components/lib/components/Typography';

import Upload from '@decisiv/iconix/lib/components/Upload';
import Table from '@decisiv/iconix/lib/components/Table';
import Trash from '@decisiv/iconix/lib/components/Trash';

import { eventStopPropagation } from 'utils/eventStopPropagation';

import { Row, HiddenFileInput, FileCard, FileErrorRow } from '../styles';
import { useBulkAddAssetContext } from '../context/BulkAddAssetContext';
import { formatFileSize } from '../utils/formatFileSize';
import { MAX_ASSET_ROWS } from '../utils/validateBulkAssetFile';

export default function StepOne() {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadedFile, setUploadedFile, fileError } = useBulkAddAssetContext();

  const fileErrorMessage = fileError
    ? {
        INVALID_TYPE: t`Invalid file type. Please upload a .csv file.`,
        INVALID_CSV: t`We could not read this CSV. Please verify the format matches the template.`,
        TOO_LARGE: t`File exceeds the maximum size of 2 MB`,
        EMPTY: t`This file has no asset rows. Add at least one asset and try again.`,
        TOO_MANY_ROWS: t`File exceeds the maximum of ${MAX_ASSET_ROWS.toLocaleString()} Assets`,
        EMPTY_VINS: t`One or more rows are missing a VIN. Please add a VIN to each row and try again.`,
      }[fileError]
    : null;

  const handleOnFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      /* istanbul ignore if -- @preserve */
      if (!file) {
        return;
      }

      setUploadedFile(file);
    },
    [setUploadedFile],
  );

  const handleOnUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleOnDeleteFile = useCallback(() => {
    setUploadedFile(null);
    // A file input is always mounted while the remove button is shown, so the
    // else (ref unset) is unreachable defensive code.
    /* istanbul ignore else -- @preserve */
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [setUploadedFile]);

  const handleDownloadTemplateClick = useCallback(() => {
    window.location.assign(
      `${import.meta.env.VITE_BFF_API_URL}/assets/bulk_add_asset_template`,
    );
  }, []);

  const hasFileSizeError = fileError === 'TOO_LARGE';

  return (
    <Flex flexDirection="column">
      <H3>
        <Trans>List of Assets</Trans>
      </H3>
      <P size="small" shade={1} marginTop={0.5}>
        <Trans>
          Select a CSV file to upload and add one or more assets to the
          account(s) and depot(s).
        </Trans>
      </P>

      <H4 marginTop={2}>
        <Trans>Upload File</Trans>
      </H4>

      {!uploadedFile && (
        <>
          <P size="small" shade={1} marginTop={0.5}>
            <Trans>Upload a CSV file to add assets</Trans>
          </P>

          <Row>
            <Button
              icon={Upload}
              text={t`Upload CSV File`}
              size="small"
              kind="secondary"
              type="button"
              onClick={handleOnUploadClick}
            />
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleOnFileChange}
              onClick={eventStopPropagation}
            />
            <Button
              icon={Table}
              text={t`Download CSV Template`}
              size="small"
              type="button"
              variant="ghost"
              onClick={handleDownloadTemplateClick}
            />
          </Row>

          <Flex alignItems="center" marginTop={0.5}>
            <Message intent="help" size="small">
              <Trans>
                Max file size 2 MB and/or 10,000 Assets. One VIN per row
                required.
              </Trans>
            </Message>
          </Flex>
        </>
      )}

      {uploadedFile && (
        <>
          <FileCard hasError={!!fileError}>
            <Flex flexDirection="column" flex={1}>
              <P size="small" weight="semibold">
                {uploadedFile.name}
              </P>
              <P
                size="small"
                shade={hasFileSizeError ? undefined : 1}
                color={hasFileSizeError ? 'danger' : undefined}
              >
                {formatFileSize(uploadedFile.size)}
              </P>
            </Flex>
            <Button
              icon={Trash}
              size="small"
              kind="secondary"
              variant="ghost"
              onClick={handleOnDeleteFile}
              aria-label={t`Remove file`}
            />
          </FileCard>

          {fileErrorMessage && (
            <FileErrorRow>
              <Message intent="warning" size="small">
                {fileErrorMessage}
              </Message>
            </FileErrorRow>
          )}

          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleOnFileChange}
            onClick={eventStopPropagation}
          />
        </>
      )}
    </Flex>
  );
}
